// Typed wrappers for the Phase-7 Buckzy endpoints.
//
// BFF rule (CLAUDE.md rule 1, INV-bff-single-origin): every call goes through
// apiClient — the single axios instance that injects Authorization: Bearer
// <token> (in-memory) and X-Merchant-ID. Never construct a new axios instance,
// and never call a Buckzy origin from the browser — Buckzy is server-side only.
import { apiClient } from "./client";

// ---------------------------------------------------------------------------
// Credential payload / result interfaces
// ---------------------------------------------------------------------------

/**
 * BuckzyCredentialPayload — write-only Buckzy credential field set.
 *
 * The field set is [ASSUMED] from the Corefy connector docs (07-RESEARCH A1)
 * pending the OQ-01 Buckzy partner API contract. If OQ-01 reveals a different
 * field set, a follow-up gap-closure task adjusts this interface and the modal;
 * the security invariants (no re-exposure, BFF-only) never change.
 */
export interface BuckzyCredentialPayload {
  client_id: string;
  client_secret: string;
  api_key: string;
  username: string;
  password: string;
  api_url: string;
  auth_url: string;
  /** LEGACY field name (kept for backward compat). Use merchant_party_id. */
  creditor_reference_id?: string;
  /**
   * Buckzy party reference for the merchant itself — debtor.reference on
   * outbound payouts. Optional at the API level: collections work without it,
   * payouts surface a clear error at Initiate time when it's missing.
   * AUDIT-PASS-9 follow-up.
   */
  merchant_party_id?: string;
  /**
   * Buckzy settlement account reference — debtorAccount.reference and
   * settlementInformation.settlementAccount.reference on payouts. Required
   * for payouts; same optional-at-API rationale as merchant_party_id.
   */
  settlement_account_id?: string;
}

/**
 * BuckzyCredentialResult — POST /v1/merchants/:id/buckzy-credentials response.
 * Never echoes any credential field (Pitfall 6 — no re-exposure).
 */
export interface BuckzyCredentialResult {
  buckzy_credential_id: number;
  merchant_id: number;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Balance interfaces
// ---------------------------------------------------------------------------

/**
 * RawMerchantBalance — the JSON shape as returned by axios.
 *
 * amount_minor crosses the wire as a JSON STRING (bigint-safe past the JS
 * 53-bit number limit); the backend serialises it with strconv.FormatInt.
 * Used only at the deserialization boundary inside getBalance.
 */
interface RawMerchantBalance {
  amount_minor: string | null;
  currency: string | null;
  fetched_at: string | null;
  source: "cache" | "live" | "not_connected";
}

/**
 * MerchantBalance — the typed balance record exposed to UI consumers.
 *
 * amount_minor is bigint (INR paise) — CLAUDE.md rule 6 / INV-money-bigint-minor.
 * It is null when the merchant has no Buckzy connection (source="not_connected").
 * Display via fmtCcyMinor — never float-coerce.
 */
export interface MerchantBalance {
  amount_minor: bigint | null;
  currency: string | null;
  fetched_at: string | null;
  source: "cache" | "live" | "not_connected";
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * createBuckzyCredential — POST /v1/merchants/:merchantId/buckzy-credentials
 * Stores an envelope-encrypted Buckzy credential set for the merchant.
 * Requires owner membership. The response never echoes credential fields.
 */
export async function createBuckzyCredential(
  merchantId: number,
  payload: BuckzyCredentialPayload,
): Promise<BuckzyCredentialResult> {
  const resp = await apiClient.post<BuckzyCredentialResult>(
    `/v1/merchants/${merchantId}/buckzy-credentials`,
    payload,
  );
  return resp.data;
}

/**
 * getBalance — GET /v1/merchants/:merchantId/balance
 * Returns the merchant's cached-or-live Buckzy balance. Requires viewer+
 * membership.
 *
 * Converts amount_minor from a JSON string to bigint at the deserialization
 * boundary (CLAUDE.md rule 6). A null amount_minor (source="not_connected")
 * stays null — no BigInt(null) coercion, no float coercion.
 */
export async function getBalance(
  merchantId: number,
): Promise<MerchantBalance> {
  const resp = await apiClient.get<RawMerchantBalance>(
    `/v1/merchants/${merchantId}/balance`,
  );
  const raw = resp.data;
  return {
    amount_minor: raw.amount_minor != null ? BigInt(raw.amount_minor) : null,
    currency: raw.currency,
    fetched_at: raw.fetched_at,
    source: raw.source,
  };
}
